import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, 
  ArrowRight,
  Clock, 
  CheckCircle,
  XCircle,
  AlertTriangle,
  Sparkles,
  MessageSquare
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function QuizTake() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const getAuthImageUrl = (url) => {
    if (!url || !token) return url;
    let normalizedUrl = url;
    if (url.includes('/api/')) {
      const apiPath = url.substring(url.indexOf('/api/') + 4);
      normalizedUrl = `${API}${apiPath}`;
    }
    const separator = normalizedUrl.includes('?') ? '&' : '?';
    return `${normalizedUrl}${separator}token=${token}`;
  };

  useEffect(() => {
    fetchQuiz();
    checkExistingResult();
  }, [quizId]);

  // Timer effect
  useEffect(() => {
    if (quiz?.is_timed && timeLeft !== null && timeLeft > 0 && !result && !alreadySubmitted) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quiz, timeLeft, result, alreadySubmitted]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}`, { headers });
      setQuiz(response.data);
      setStartedAt(new Date().toISOString());
      if (response.data.is_timed && response.data.time_limit_minutes) {
        setTimeLeft(response.data.time_limit_minutes * 60);
      }
    } catch (error) {
      toast.error("Failed to load quiz");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingResult = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}/my-result`, { headers });
      if (response.data.submitted) {
        setResult(response.data);
        setAlreadySubmitted(true);
      }
    } catch (error) {
      // No existing result
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    if (submitting || alreadySubmitted) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/quizzes/${quizId}/submit`,
        { answers, started_at: startedAt },
        { headers }
      );
      setResult(response.data);
      toast.success("Quiz submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getProgressPercentage = () => {
    if (!quiz?.questions) return 0;
    const answered = Object.keys(answers).length;
    return (answered / quiz.questions.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show result screen
  if (result || alreadySubmitted) {
    const displayResult = result || {};
    return (
      <div className="min-h-screen bg-background" data-testid="quiz-result">
        <header className="sticky top-0 z-50 glass border-b border-border/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="ml-4 text-lg font-bold text-foreground font-['Manrope']">Quiz Result</h1>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${
              displayResult.percentage >= 70 ? "bg-green-100" : displayResult.percentage >= 50 ? "bg-yellow-100" : "bg-red-100"
            }`}>
              {displayResult.percentage >= 70 ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : displayResult.percentage >= 50 ? (
                <AlertTriangle className="w-12 h-12 text-yellow-600" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-foreground font-['Manrope']">
              {displayResult.score} / {displayResult.total_points}
            </h2>
            <p className="text-xl text-muted-foreground mt-2">
              {displayResult.percentage?.toFixed(1)}% Score
            </p>
            <p className={`text-lg font-medium mt-2 ${
              displayResult.percentage >= 70 ? "text-green-600" : displayResult.percentage >= 50 ? "text-yellow-600" : "text-red-600"
            }`}>
              {displayResult.percentage >= 70 ? "Great job!" : displayResult.percentage >= 50 ? "Good effort!" : "Keep practicing!"}
            </p>
            {displayResult.completion_time_seconds && (
              <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <Clock className="w-4 h-4" />
                Completed in {Math.floor(displayResult.completion_time_seconds / 60)}m {displayResult.completion_time_seconds % 60}s
              </p>
            )}
          </motion.div>

          {/* Review Questions */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Review Answers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {quiz?.questions.map((question, index) => {
                const userAnswer = displayResult.answers?.[question.question_id] || "";
                const correctAnswer = displayResult.correct_answers?.[question.question_id] || "";
                const isCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();
                
                return (
                  <div
                    key={question.question_id}
                    className={`p-4 rounded-xl border ${
                      isCorrect ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                    }`}
                    data-testid={`result-question-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      {isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-foreground whitespace-pre-wrap">{question.question_text}</p>
                        {question.image_url && (
                          <img src={getAuthImageUrl(question.image_url)} alt="Question" className="max-h-40 rounded-lg mt-2 border" />
                        )}
                        <p className="text-sm mt-2">
                          <span className="text-muted-foreground">Your answer: </span>
                          <span className={isCorrect ? "text-green-600" : "text-red-600"}>
                            {userAnswer || "(No answer)"}
                          </span>
                        </p>
                        {!isCorrect && (
                          <p className="text-sm mt-1">
                            <span className="text-muted-foreground">Correct answer: </span>
                            <span className="text-green-600 font-medium">{correctAnswer}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <Button
              onClick={() => navigate(-1)}
              className="rounded-full bg-primary text-primary-foreground"
              data-testid="back-to-class-btn"
            >
              Back to Class
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const currentQ = quiz?.questions?.[currentQuestion];

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-take">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-bold text-foreground font-['Manrope']">{quiz?.title}</h1>
            </div>
            {quiz?.is_timed && timeLeft !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                timeLeft < 60 ? "bg-red-100 text-red-600 timer-warning" : "bg-muted"
              }`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold" data-testid="timer">{formatTime(timeLeft)}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-muted/30 border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestion + 1} of {quiz?.questions.length}</span>
            <span>{Object.keys(answers).length} answered</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {currentQ?.question_type === "multiple_choice" ? "Multiple Choice" : 
                     currentQ?.question_type === "true_false" ? "True/False" : "Short Answer"}
                  </span>
                  <span className="text-sm font-medium">{currentQ?.points} point{currentQ?.points !== 1 ? "s" : ""}</span>
                </div>
                <CardTitle className="text-xl font-['Manrope'] whitespace-pre-wrap">{currentQ?.question_text}</CardTitle>
                {currentQ?.image_url && (
                  <img src={getAuthImageUrl(currentQ.image_url)} alt="Question" className="max-h-64 rounded-lg mt-3 border" />
                )}
              </CardHeader>
              <CardContent>
                {currentQ?.question_type === "multiple_choice" && currentQ?.options && (
                  <RadioGroup
                    value={answers[currentQ.question_id] || ""}
                    onValueChange={(value) => handleAnswerChange(currentQ.question_id, value)}
                    className="space-y-3"
                  >
                    {currentQ.options.map((option, i) => (
                      <Label
                        key={i}
                        htmlFor={`option-${i}`}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all quiz-option ${
                          answers[currentQ.question_id] === option ? "selected" : ""
                        }`}
                        data-testid={`option-${i}`}
                      >
                        <RadioGroupItem value={option} id={`option-${i}`} />
                        <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span>{option}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                )}

                {currentQ?.question_type === "true_false" && (
                  <RadioGroup
                    value={answers[currentQ.question_id] || ""}
                    onValueChange={(value) => handleAnswerChange(currentQ.question_id, value)}
                    className="space-y-3"
                  >
                    <Label
                      htmlFor="true"
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all quiz-option ${
                        answers[currentQ.question_id] === "true" ? "selected" : ""
                      }`}
                      data-testid="option-true"
                    >
                      <RadioGroupItem value="true" id="true" />
                      <span>True</span>
                    </Label>
                    <Label
                      htmlFor="false"
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all quiz-option ${
                        answers[currentQ.question_id] === "false" ? "selected" : ""
                      }`}
                      data-testid="option-false"
                    >
                      <RadioGroupItem value="false" id="false" />
                      <span>False</span>
                    </Label>
                  </RadioGroup>
                )}

                {currentQ?.question_type === "short_answer" && (
                  <Input
                    placeholder="Type your answer..."
                    value={answers[currentQ.question_id] || ""}
                    onChange={(e) => handleAnswerChange(currentQ.question_id, e.target.value)}
                    className="h-12"
                    data-testid="short-answer-input"
                  />
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            className="rounded-full"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentQuestion === quiz?.questions.length - 1 ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="rounded-full bg-primary text-primary-foreground"
                  disabled={submitting}
                  data-testid="submit-quiz-btn"
                >
                  {submitting ? "Submitting..." : "Submit Quiz"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-['Manrope']">Submit Quiz?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {(() => {
                      const answered = Object.keys(answers).length;
                      const total = quiz?.questions?.length || 0;
                      const unanswered = total - answered;
                      return unanswered > 0
                        ? `Are you sure you want to submit the quiz? You have ${unanswered} unanswered question${unanswered > 1 ? "s" : ""}. You won't be able to make changes after submission.`
                        : "Are you sure you want to submit the quiz? You won't be able to make changes after submission.";
                    })()}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-submit-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSubmit} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-submit-btn">
                    Yes
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              onClick={() => setCurrentQuestion(Math.min(quiz.questions.length - 1, currentQuestion + 1))}
              className="rounded-full bg-primary text-primary-foreground"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question Navigator */}
        <div className="mt-8">
          <p className="text-sm text-muted-foreground mb-3">Jump to question:</p>
          <div className="flex flex-wrap gap-2">
            {quiz?.questions.map((q, i) => (
              <button
                key={q.question_id}
                onClick={() => setCurrentQuestion(i)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                  currentQuestion === i
                    ? "bg-primary text-primary-foreground"
                    : answers[q.question_id]
                    ? "bg-green-100 text-green-700"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                data-testid={`jump-to-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
